import { useMemo } from "react";
import { useAuth } from "@/features/auth/context/auth-context";

export interface AuthGuardState {
  isValidating: boolean;
  isValid: boolean;
  isReady: boolean;
  shouldRedirect: boolean;
  redirectTo: string;
}

/**
 * Hook simplificado que lee el estado de validación del contexto de autenticación.
 * isReady evita redirecciones prematuras mientras se valida el token por primera vez.
 */
export const useAuthGuard = (): AuthGuardState => {
  const { token, selectedSubscriptionId, selectedCompanyId, isValidating, isValid, isReady } = useAuth();

  return useMemo(() => {
    const hasToken = !!token;
    const hasValidSession = hasToken && isValid;

    // Solo redirigir si ya pasó la validación inicial (isReady)
    const shouldRedirect = isReady && !isValidating && (!hasToken || !hasValidSession);

    const redirectTo = shouldRedirect
      ? "/login"
      : hasValidSession && !selectedSubscriptionId
        ? "/select-subscription"
        : hasValidSession && selectedSubscriptionId !== "SYSTEM" && !selectedCompanyId
          ? "/select-company"
          : "";

    return {
      isValidating,
      isValid: hasValidSession,
      isReady,
      shouldRedirect,
      redirectTo,
    };
  }, [isValidating, isValid, isReady, token, selectedSubscriptionId, selectedCompanyId]);
};
